<template>
  <div class="teacher-page d-flex">
    <aside class="side-nav" :class="{ collapsed: sidebarCollapsed }">
      <div class="side-head">
        <h4 class="mb-0" v-show="!sidebarCollapsed">教师端</h4>
        <button type="button" class="collapse-btn" @click="toggleSidebar">{{ sidebarCollapsed ? '>>' : '<<' }}</button>
      </div>
      <button type="button" class="sub-btn" @click="goQuestionManage"><span class="nav-text">题库管理</span></button>
      <button type="button" class="sub-btn" @click="goPaperManage"><span class="nav-text">试卷管理</span></button>
      <button type="button" class="sub-btn" @click="goExamManage"><span class="nav-text">考试管理</span></button>
      <button type="button" class="sub-btn" @click="goGradingManage"><span class="nav-text">阅卷管理</span></button>
      <button type="button" class="sub-btn active"><span class="nav-text">考生试卷管理</span></button>
      <button type="button" class="sub-btn" @click="goProctor"><span class="nav-text">监考系统</span></button>
      <button type="button" class="sub-btn" @click="goStudentUsers"><span class="nav-text">学生用户管理</span></button>
      <div class="mt-auto">
        <button type="button" class="btn btn-sm btn-light w-100 mb-2" @click="logout">退出登录</button>
      </div>
    </aside>

    <main class="content-area">
      <header class="topbar">
        <h2>考生试卷管理</h2>
        <div class="actions">
          <span class="text-muted small">当前账号：{{ currentUser.username || '教师' }}</span>
        </div>
      </header>

      <section class="card-box">
        <h4 class="mb-3">选择考试</h4>
        <div class="row g-2 align-items-end mb-3">
          <div class="col-md-6">
            <label class="form-label small mb-1">考试</label>
            <select v-model.number="selectedExamId" class="form-select" @change="onExamChange">
              <option :value="null">请选择一场考试</option>
              <option v-for="row in examRows" :key="'ex-' + row.exam.id" :value="row.exam.id">
                {{ row.exam.examName }}（ID {{ row.exam.id }} · 参考 {{ row.participantCount || 0 }} 人）
              </option>
            </select>
          </div>
          <div class="col-md-auto">
            <button type="button" class="btn btn-light" @click="loadExamList">刷新考试列表</button>
          </div>
        </div>
        <p class="small text-muted mb-0">
          可查看该场考试每位考生的答卷状态、切屏次数与强制交卷阈值；对已交卷或<strong>强制交卷</strong>的记录可<strong>打回</strong>，使学生可再次进入考试继续作答（<strong>已阅卷</strong>的记录不可打回）。
        </p>
      </section>

      <section v-if="selectedExamId" class="card-box">
        <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <h4 class="mb-0">考生列表</h4>
          <div class="d-flex flex-wrap gap-2">
            <button type="button" class="btn btn-sm btn-success" @click="exportExamScores">导出总成绩</button>
            <button type="button" class="btn btn-sm btn-light" @click="loadRecords">刷新</button>
          </div>
        </div>
        <div class="result-shell">
          <table class="table table-striped align-middle modern-table mb-0">
            <thead>
              <tr>
                <th>考生</th>
                <th>记录ID</th>
                <th>状态</th>
                <th>总分</th>
                <th>切屏次数</th>
                <th>强制交卷阈值</th>
                <th>交卷时间</th>
                <th style="min-width: 320px">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in records" :key="'rec-' + r.id">
                <td>{{ r.username || '—' }}</td>
                <td>{{ r.id }}</td>
                <td><span class="badge bg-secondary">{{ displayRecordStatus(r) }}</span></td>
                <td>
                  <span v-if="hasRecordScore(r)" class="fw-semibold text-primary">{{ r.score }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>{{ r.screenCount != null ? r.screenCount : 0 }}</td>
                <td>{{ r.screenForceLimit == null ? '默认(5)' : r.screenForceLimit }}</td>
                <td>{{ formatDt(r.submitTime) }}</td>
                <td>
                  <div class="d-flex flex-wrap gap-1 align-items-center">
                    <button type="button" class="btn btn-sm btn-primary" @click="openAnswerDetail(r)">查看答题明细</button>
                    <button
                      type="button"
                      class="btn btn-sm btn-warning"
                      :disabled="!canReopen(r.status)"
                      @click="reopenRecord(r)"
                    >
                      打回继续作答
                    </button>
                    <div class="input-group input-group-sm er-limit-group">
                      <input
                        v-model.number="limitDraft[r.id]"
                        type="number"
                        min="1"
                        max="50"
                        class="form-control"
                        placeholder="阈值"
                      />
                      <button type="button" class="btn btn-outline-primary" @click="saveLimit(r)">保存阈值</button>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearLimit(r)">恢复默认</button>
                  </div>
                </td>
              </tr>
              <tr v-if="records.length === 0">
                <td colspan="8" class="text-muted">该考试暂无考生记录</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div v-if="answerModalOpen" class="er-modal-mask" @click.self="closeAnswerDetail">
        <div class="er-modal-panel" @click.stop>
          <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
            <div>
              <h4 class="mb-1">答题明细</h4>
              <div class="small text-muted" v-if="answerModalRecord">
                考生：<strong>{{ answerModalRecord.username }}</strong> · 记录 #{{ answerModalRecord.id }} · 状态：                <span class="badge bg-secondary">{{ displayRecordStatus(answerModalRecord) }}</span>
                <template v-if="hasRecordScore(answerModalRecord)">
                  · 答卷总分：<strong class="text-primary">{{ answerModalRecord.score }}</strong> 分
                </template>
              </div>
            </div>
            <div class="d-flex flex-wrap gap-2">
              <button
                type="button"
                class="btn btn-sm btn-success"
                :disabled="!answerModalRecord"
                @click="exportAnswerExcel"
              >
                导出 Excel
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeAnswerDetail">关闭</button>
            </div>
          </div>
          <div v-if="answerLoading" class="text-muted py-4 text-center">加载中…</div>
          <template v-else>
            <p class="small mb-2">
              下列为<strong>本场试卷全部题目</strong>及该生作答；<strong>本题满分</strong>为试卷配置分值，<strong>得分</strong>为已入库评分（客观题自动、主观/编程题以阅卷为准）。已阅卷时总分与「答卷总分」一致。
            </p>
            <p v-if="answerDetailRows.length" class="small text-muted mb-2">
              明细得分合计：<strong>{{ sumDetailScores }}</strong> 分（可与上方答卷总分校验）
            </p>
            <div class="er-detail-scroll">
              <table class="table table-sm table-bordered align-middle mb-0 er-detail-table">
                <thead class="table-light">
                  <tr>
                    <th style="width: 48px">序</th>
                    <th style="width: 88px">题型</th>
                    <th>题干</th>
                    <th style="min-width: 140px">选项</th>
                    <th>学生作答</th>
                    <th>参考答案</th>
                    <th style="width: 72px">本题满分</th>
                    <th style="width: 64px">得分</th>
                    <th style="width: 72px">是否正确</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in answerDetailRows" :key="'ad-' + row.questionId">
                    <td>{{ row.sort != null ? row.sort : idx + 1 }}</td>
                    <td>{{ row.typeText || row.type || '—' }}</td>
                    <td class="er-cell-title">
                      <div class="er-title-line">{{ row.title || '—' }}</div>
                      <div v-if="stemImgList(row).length" class="d-flex flex-wrap gap-1 mt-1">
                        <img
                          v-for="(u, ii) in stemImgList(row)"
                          :key="'st-' + row.questionId + '-' + ii"
                          :src="u"
                          class="er-q-thumb"
                          alt="题干图"
                          loading="lazy"
                          @click="openAnswerImgPreview(u)"
                        />
                      </div>
                    </td>
                    <td class="er-cell-options small">
                      <div class="mb-1">{{ formatOptionsText(row.options) }}</div>
                      <div class="d-flex flex-wrap gap-1">
                        <img
                          v-for="op in optEntriesForRow(row)"
                          v-show="op.imageUrl"
                          :key="'opim-' + row.questionId + '-' + op.key"
                          :src="op.imageUrl"
                          class="er-q-thumb"
                          :title="'选项 ' + op.key"
                          loading="lazy"
                          alt=""
                          @click="openAnswerImgPreview(op.imageUrl)"
                        />
                      </div>
                    </td>
                    <td>
                      <template v-if="String(row.type || '').toLowerCase() === MATERIAL_COLLECT_TYPE">
                        <div class="small text-muted">说明</div>
                        <pre class="er-pre mb-2">{{ row.materialCollectText || '—' }}</pre>
                        <div class="small text-muted">附件</div>
                        <ul v-if="(row.materialAttachments || []).length" class="list-unstyled small mb-0">
                          <li v-for="(a, ai) in row.materialAttachments" :key="'mat-' + row.questionId + '-' + ai">
                            <a :href="a.downloadUrl || a.viewUrl" target="_blank" rel="noopener">{{ a.originalName || '附件' }}</a>
                          </li>
                        </ul>
                        <div v-else class="text-muted small">无附件</div>
                      </template>
                      <pre v-else class="er-pre">{{ row.userAnswer || '（未作答）' }}</pre>
                    </td>
                    <td><pre class="er-pre er-pre-answer">{{ row.correctAnswer || '—' }}</pre></td>
                    <td class="text-center">{{ row.maxScore != null ? row.maxScore : row.score }}</td>
                    <td class="text-center fw-semibold">{{ row.answerScore != null ? row.answerScore : 0 }}</td>
                    <td class="text-center small">{{ objectiveCorrectText(row) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="!answerDetailRows.length" class="text-muted py-3">暂无题目数据（请确认考试已关联试卷且含试题）。</div>
          </template>
        </div>
      </div>
    </main>

    <div v-if="answerImgPreview" class="er-img-mask" @click.self="closeAnswerImgPreview">
      <div class="er-img-panel" @click.stop @wheel.prevent="onAnswerImgWheel">
        <div class="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
          <span class="small text-muted">滚轮或按钮缩放</span>
          <div class="d-flex flex-wrap gap-1">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="answerImgScale = Math.min(4, answerImgScale + 0.2)">放大</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="answerImgScale = Math.max(0.25, answerImgScale - 0.2)">缩小</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="answerImgScale = 1">复位</button>
            <button type="button" class="btn btn-sm btn-light" @click="closeAnswerImgPreview">关闭</button>
          </div>
        </div>
        <div class="er-img-viewport">
          <img
            :src="answerImgPreview"
            alt="预览"
            class="er-img-zoomed"
            :style="{ transform: 'scale(' + answerImgScale + ')' }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { appConfirm } from '../utils/appDialog'
import { parseUrlList, optionEntriesFromRow as buildQuestionOptionEntries } from '../utils/questionRichMedia'
import { MATERIAL_COLLECT_TYPE } from '../utils/materialCollect'

export default {
  name: 'TeacherExamRecordManageView',
  data() {
    return {
      MATERIAL_COLLECT_TYPE,
      sidebarCollapsed: false,
      currentUser: {},
      examRows: [],
      selectedExamId: null,
      records: [],
      limitDraft: {},
      answerModalOpen: false,
      answerLoading: false,
      answerModalRecord: null,
      answerDetailRows: [],
      answerImgPreview: null,
      answerImgScale: 1
    }
  },
  computed: {
    sumDetailScores() {
      return this.answerDetailRows.reduce((s, row) => s + (Number(row.answerScore) || 0), 0)
    }
  },
  mounted() {
    try {
      this.currentUser = JSON.parse(localStorage.getItem('exam_user') || '{}')
    } catch (e) {
      this.currentUser = {}
    }
    this.loadExamList()
  },
  methods: {
    authHeaders(extra = {}) {
      return { 'X-Role': 'teacher', 'X-CSRF-Token': 'teacher-ui', ...extra }
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    logout() {
      localStorage.removeItem('exam_user')
      window.location.hash = '#/'
    },
    goQuestionManage() {
      window.location.hash = '#/teacher/questions'
    },
    goPaperManage() {
      localStorage.setItem('teacher_active_menu', 'paper')
      window.location.hash = '#/teacher/papers'
    },
    goExamManage() {
      localStorage.setItem('teacher_active_menu', 'exam')
      window.location.hash = '#/teacher/papers'
    },
    goGradingManage() {
      localStorage.setItem('teacher_active_menu', 'grading')
      window.location.hash = '#/teacher/papers'
    },
    goProctor() {
      window.location.hash = '#/teacher/proctor'
    },
    goStudentUsers() {
      window.location.hash = '#/teacher/students'
    },
    loadExamList() {
      const params = new URLSearchParams({ page: '0', size: '200' })
      fetch(`/api/teacher/exam-manage/list?${params.toString()}`, { headers: this.authHeaders() })
        .then((r) => r.json())
        .then((res) => {
          const data = res.data
          this.examRows = Array.isArray(data) ? data : ((data && data.content) || [])
        })
        .catch(() => {
          this.examRows = []
        })
    },
    onExamChange() {
      this.records = []
      this.limitDraft = {}
      if (this.selectedExamId) this.loadRecords()
    },
    loadRecords() {
      if (!this.selectedExamId) return
      fetch(`/api/teacher/exam-record-manage/records?examId=${this.selectedExamId}`, {
        headers: this.authHeaders()
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '加载失败')
            this.records = []
            return
          }
          this.records = res.data || []
          const d = { ...this.limitDraft }
          this.records.forEach((row) => {
            if (d[row.id] === undefined) {
              d[row.id] = row.screenForceLimit == null ? 5 : row.screenForceLimit
            }
          })
          this.limitDraft = d
        })
        .catch(() => {
          this.records = []
          window.alert('网络错误')
        })
    },
    formatDt(v) {
      if (!v) return '—'
      try {
        return String(v).replace('T', ' ').slice(0, 19)
      } catch (e) {
        return '—'
      }
    },
    displayRecordStatus(row) {
      if (row && row.statusText) return row.statusText
      return this.recordStatusText(row && row.status)
    },
    recordStatusText(s) {
      const u = String(s || '').trim().toUpperCase()
      if (!u) return '—'
      if (u === 'ASSIGNED') return '已分配（可作答）'
      if (u === 'EXAMING') return '作答中'
      if (u === 'COMMIT') return '已交卷'
      if (u === 'FORCE_END') return '强制交卷'
      if (u === 'GRADED') return '已阅卷'
      return '其它状态'
    },
    hasRecordScore(r) {
      if (!r || r.score == null) return false
      const st = String(r.status || '').toUpperCase()
      if (st === 'GRADED' || st === 'COMMIT' || st === 'FORCE_END') return true
      return Number(r.score) > 0
    },
    openAnswerDetail(r) {
      this.answerModalRecord = r
      this.answerModalOpen = true
      this.answerDetailRows = []
      this.answerLoading = true
      fetch(`/api/student/exam/answers/record/${r.id}`, {
        headers: this.authHeaders()
      })
        .then((x) => x.json())
        .then((res) => {
          this.answerLoading = false
          if (res.code !== 200) {
            window.alert(res.message || '加载答题明细失败')
            this.answerDetailRows = []
            return
          }
          this.answerDetailRows = Array.isArray(res.data) ? res.data : []
        })
        .catch(() => {
          this.answerLoading = false
          window.alert('网络错误')
          this.answerDetailRows = []
        })
    },
    closeAnswerDetail() {
      this.answerModalOpen = false
      this.answerModalRecord = null
      this.answerDetailRows = []
      this.answerLoading = false
      this.closeAnswerImgPreview()
    },
    stemImgList(row) {
      return parseUrlList(row && row.contentImages)
    },
    optEntriesForRow(row) {
      return buildQuestionOptionEntries(row)
    },
    openAnswerImgPreview(u) {
      const s = String(u || '').trim()
      if (!s) return
      this.answerImgScale = 1
      this.answerImgPreview = s
    },
    closeAnswerImgPreview() {
      this.answerImgPreview = null
      this.answerImgScale = 1
    },
    onAnswerImgWheel(e) {
      if (!this.answerImgPreview) return
      const dy = e.deltaY || 0
      const step = dy > 0 ? -0.12 : 0.12
      this.answerImgScale = Math.min(4, Math.max(0.25, this.answerImgScale + step))
    },
    exportAnswerExcel() {
      const r = this.answerModalRecord
      if (!r || !r.id) return
      const safeName = String(r.username || 'student')
        .replace(/[/\\?%*:|"<>]/g, '_')
        .slice(0, 40)
      fetch(`/api/student/exam/answers/record/${r.id}/export`, {
        headers: this.authHeaders()
      })
        .then(async (res) => {
          if (!res.ok) {
            const j = await res.json().catch(() => ({}))
            throw new Error((j && j.message) || `导出失败（${res.status}）`)
          }
          return res.blob()
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `作答明细_${safeName}_记录${r.id}.xlsx`
          a.click()
          URL.revokeObjectURL(url)
        })
        .catch((e) => window.alert(e.message || '导出失败'))
    },
    exportExamScores() {
      if (!this.selectedExamId) {
        window.alert('请先选择考试')
        return
      }
      const examId = this.selectedExamId
      fetch(`/api/student/exam/scores/exam/${examId}/export`, {
        headers: this.authHeaders()
      })
        .then(async (res) => {
          if (!res.ok) {
            const j = await res.json().catch(() => ({}))
            throw new Error((j && j.message) || `导出失败（${res.status}）`)
          }
          return res.blob()
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `exam_scores_${examId}.xlsx`
          a.click()
          URL.revokeObjectURL(url)
        })
        .catch((e) => window.alert(e.message || '导出总成绩失败'))
    },
    formatOptionsText(raw) {
      if (raw == null || String(raw).trim() === '') return '—'
      try {
        const o = JSON.parse(String(raw))
        if (o && typeof o === 'object' && !Array.isArray(o)) {
          return Object.keys(o)
            .sort()
            .map((k) => `${k}. ${o[k]}`)
            .join('\n')
        }
      } catch (e) {
        void e
      }
      return String(raw)
    },
    objectiveCorrectText(row) {
      const t = String(row.type || '').toLowerCase()
      if (t === 'essay' || t === 'fill' || t === 'blank' || t === 'coding' || t === 'subjective') return '—'
      if (t !== 'radio' && t !== 'multiple' && t !== 'judge') return '—'
      const ic = row.answerIsCorrect
      if (ic === 1 || ic === true) return '正确'
      if (ic === 0 || ic === false) return '错误'
      return '—'
    },
    canReopen(status) {
      const u = String(status || '').toUpperCase()
      return u === 'COMMIT' || u === 'FORCE_END'
    },
    async reopenRecord(r) {
      if (!this.canReopen(r.status)) return
      if (
        !(await appConfirm(
          `确定将考生「${r.username}」的答卷打回吗？\n学生将可再次进入本场考试继续作答。`
        ))
      ) {
        return
      }
      const reset = await appConfirm(
        '是否将切屏累计次数清零？\n「确定」= 清零（推荐强制交卷后），「取消」= 保留当前次数'
      )
      fetch(`/api/teacher/exam-record-manage/records/${r.id}/reopen`, {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ resetScreenCount: reset })
      })
        .then((x) => x.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '打回失败')
            return
          }
          window.alert('已打回，学生刷新考试列表后即可再次进入。')
          this.loadRecords()
        })
        .catch(() => window.alert('网络错误'))
    },
    saveLimit(r) {
      const v = this.limitDraft[r.id]
      const n = Number(v)
      if (Number.isNaN(n) || n < 1 || n > 50) {
        window.alert('阈值请输入 1～50 的整数')
        return
      }
      fetch(`/api/teacher/exam-record-manage/records/${r.id}/screen-force-limit`, {
        method: 'PUT',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ screenForceLimit: n })
      })
        .then((x) => x.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '保存失败')
            return
          }
          this.loadRecords()
        })
        .catch(() => window.alert('网络错误'))
    },
    clearLimit(r) {
      fetch(`/api/teacher/exam-record-manage/records/${r.id}/screen-force-limit`, {
        method: 'PUT',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ screenForceLimit: null })
      })
        .then((x) => x.json())
        .then((res) => {
          if (res.code !== 200) {
            window.alert(res.message || '操作失败')
            return
          }
          this.limitDraft = { ...this.limitDraft, [r.id]: 5 }
          this.loadRecords()
        })
        .catch(() => window.alert('网络错误'))
    }
  }
}
</script>

<style scoped>
.teacher-page {
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(circle at 8% 10%, rgba(82, 149, 255, 0.16), transparent 34%),
    radial-gradient(circle at 92% 20%, rgba(120, 170, 255, 0.14), transparent 30%),
    linear-gradient(160deg, #f7faff 0%, #eef4ff 55%, #f8fbff 100%);
  color: #1f2a44;
}
.side-nav {
  width: 240px;
  min-height: calc(100vh - 40px);
  margin-right: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid #d8e6ff;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(63, 108, 199, 0.1);
  flex-shrink: 0;
}
.side-nav.collapsed {
  width: 76px;
}
.side-nav.collapsed .nav-text {
  display: none;
}
.side-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.collapse-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #d8e6ff;
  background: #f6f9ff;
  color: #335896;
  font-size: 12px;
}
.sub-btn {
  border: 1px solid #d8e6ff;
  border-radius: 10px;
  padding: 10px 12px;
  color: #335896;
  background: #f6f9ff;
  text-align: left;
  transition: all 0.2s ease;
}
.sub-btn:hover {
  background: #eef5ff;
  border-color: #bfd6ff;
}
.sub-btn.active {
  background: linear-gradient(120deg, #2f6feb, #5ca2ff);
  color: #fff;
  border-color: transparent;
}
.content-area {
  flex: 1;
  min-width: 0;
}
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92));
  border: 1px solid #d8e6ff;
  box-shadow: 0 10px 26px rgba(48, 96, 192, 0.1);
}
.card-box {
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.92));
  border: 1px solid #d8e6ff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 10px 26px rgba(48, 96, 192, 0.1);
}
.result-shell {
  border: 1px solid rgba(145, 180, 240, 0.35);
  border-radius: 12px;
  padding: 12px;
  overflow-x: auto;
}
.modern-table {
  font-size: 0.9rem;
}
@media (max-width: 992px) {
  .teacher-page {
    flex-direction: column;
  }
  .side-nav {
    width: 100%;
    min-height: auto;
    margin-right: 0;
  }
}

.er-limit-group {
  width: 148px;
  min-width: 148px;
}

.er-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 30, 60, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 12px;
  overflow-y: auto;
}

.er-modal-panel {
  width: min(1100px, 100%);
  max-height: min(92vh, 900px);
  display: flex;
  flex-direction: column;
  background: linear-gradient(165deg, #ffffff, #f5f9ff);
  border: 1px solid #c5d6f5;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
}

.er-detail-scroll {
  overflow: auto;
  max-height: min(70vh, 640px);
  border: 1px solid #dee8fb;
  border-radius: 10px;
}

.er-detail-table {
  font-size: 0.8125rem;
  white-space: normal;
}

.er-cell-title {
  max-width: 220px;
  white-space: pre-wrap;
  word-break: break-word;
}

.er-cell-options {
  max-width: 200px;
  white-space: pre-wrap;
  word-break: break-word;
}

.er-pre {
  margin: 0;
  max-height: 160px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.75rem;
  line-height: 1.4;
  background: #f8fafc;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.er-pre-answer {
  background: #fffbeb;
  border-color: #fde68a;
}

.er-q-thumb {
  max-width: 120px;
  max-height: 72px;
  object-fit: contain;
  border-radius: 6px;
  border: 1px solid #c5d6f5;
  cursor: zoom-in;
  background: #fff;
}

.er-img-mask {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.er-img-panel {
  background: #fff;
  border-radius: 12px;
  padding: 12px 14px 14px;
  max-width: min(96vw, 960px);
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.35);
}

.er-img-viewport {
  overflow: auto;
  max-height: min(78vh, 760px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #f1f5f9;
  border-radius: 8px;
}

.er-img-zoomed {
  max-width: min(90vw, 900px);
  height: auto;
  transform-origin: center center;
  transition: transform 0.08s ease-out;
}
</style>
